<style>
h1 {
  @apply leading-tight tracking-tight text-center whitespace-pre-wrap text-4xl font-semibold;
}
h2 {
  @apply leading-tight tracking-tight text-center whitespace-pre-wrap text-2xl font-semibold;
}
.external-link {
  color: var(--primary);
}
.hero {
  @apply w-1/2 bg-cover hidden;
  background-image: var(--registration-hero-path);
}
@screen md {
  .hero {
    @apply block;
  }
}
</style>

<script>
import { tick, onMount } from "svelte"
import queryString from "query-string"
import { navigate, link } from "svelte-routing"

import API, { anonymousApi, handleErrors } from "@local/utils/api"

import {
  partnerId as _partnerId,
  partner as _partner,
  externalLogin,
  registrationPageTitle,
  registrationDisabled,
  registrationInfoPath,
  registrationPageShowInfoBox,
  registrationPageShowBanner,
  registrationInfoBoxHeader,
  registrationInfoBoxDescription,
} from "@local/store/theme"

import { auth } from "@local/store/auth"
import { workflowLang } from "@shared/store/workflowKey"
import { sanitize } from "@shared/utils/sanitize"

import Link from "@local/components/Link.svelte"
import TextInput from "@local/components/TextInput.svelte"
import FullLayout from "@shared/components/FullLayout.svelte"
import Heading from "@local/components/Heading.svelte"
import Submit from "@local/components/Submit.svelte"
import {
  emailText,
  passwordText,
  renewingOrAlreadyRegisteredText,
  nextText,
  createYourPasswordWithAtLeastXCharacteres,
  andText,
  byRegisteringYouAgreeToTheText,
  termsOfServiceText,
  privacyPolicyText,
} from "@shared/utils/translations"
import { setPartner } from "@local/store/theme"
import { referral as _referral } from "@local/store/referral"
import { referralUtms as _referralUtms } from "@local/store/referralUtms"

export let location = {}

let queryParams
$: queryParams = queryString.parse(location.search)
$: next = queryParams.next
$: lang = queryParams.lang || $workflowLang

$: partnerId = $_partnerId

$: setPartner($_partner, $_referral)

$: safeInfoBoxHeader = sanitize($registrationInfoBoxHeader)
$: safeInfoBoxDescription = sanitize($registrationInfoBoxDescription)

onMount(async () => {
  if ($registrationDisabled || $externalLogin) {
    navigate("/")
  }
})

let form = {
  email: "",
  password: "",
}
let errors = {}

let subdomain = window.location.hostname.split(".").shift() || ""
let loading = false

const hideAlreadyRegistered = ["aurora", "cccc", "ccct"].includes(subdomain)

const customErrMsgs = {
  email: {
    alreadyExists: `You already have an account with us.${
      hideAlreadyRegistered
        ? ""
        : " Please click below on ‘Already registered’ to continue."
    } `,
    alreadyExistsInOtherPartner: `You already have an account with other partner. Please call us at 1-866-888-7013`,
  },
}

function trackConversion() {
  window.dataLayer = window.dataLayer || []

  function gtag() {
    dataLayer.push(arguments)
  }

  gtag("event", "conversion", {
    send_to: "AW-732075948/YRY5CJfKn8ECEKyvit0C",
  })
}

async function submit() {
  if (loading) return
  loading = true

  try {
    let referralUtmSource = queryParams?.utm_source
    if (!referralUtmSource) {
      referralUtmSource = $_referralUtms?.referralUtmSource
    }
    let referralUtmCampaign = queryParams?.utm_campaign
    if (!referralUtmCampaign) {
      referralUtmCampaign = $_referralUtms?.referralUtmCampaign
    }

    const query = new URLSearchParams()
    if (next) query.set("next", next)
    if (referralUtmSource) query.set("referralUtmSource", referralUtmSource)
    if (referralUtmCampaign)
      query.set("referralUtmCampaign", referralUtmCampaign)

    const path = `partners/${partnerId}/users${
      query.toString() ? `?${query.toString()}` : ""
    }`
    const tokens = await anonymousApi
      .post(path, {
        json: {
          ...form,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          partnerOrigin: subdomain,
          lang,
          referralCode: $_referral?.code || null,
        },
      })
      .json()
    auth(tokens)
    trackConversion()

    API.post("api/campaigner", {
      json: { email: form.email, funnel: subdomain },
    })
      .then()
      .catch((err) =>
        console.log(
          `Failure to send ${form.email} to Campaigner subscribers list. ${err}`
        )
      )

    await tick()

    const queryNext = new URLSearchParams()
    if (referralUtmSource) queryNext.set("utm_source", referralUtmSource)
    if (referralUtmCampaign) queryNext.set("utm_campaign", referralUtmCampaign)

    const nextPath = `${next || "/"}${
      queryNext.toString() ? `?${queryNext.toString()}` : ""
    }`

    navigate(nextPath, { replace: true })
  } catch (err) {
    errors = await handleErrors({ err, customErrMsgs })
  } finally {
    loading = false
  }
}
</script>

<FullLayout>
  <div class="flex min-h-screen bg-white">
    <div class="hero"></div>
    <div class="md:w-1/2 max-w-xl w-full mx-auto my-24 px-4 py-5 shadow-none">
      <form on:submit|preventDefault autocomplete="off" on:submit="{submit}">
        <h1 class="mb-6">{$registrationPageTitle}</h1>
        {#if $registrationPageShowBanner}
          <img src="{$registrationInfoPath}" alt="Info" class="mb-8" />
        {/if}

        <TextInput
          name="email"
          label="{emailText[lang]}*"
          bind:value="{form.email}"
          errors="{errors.email}"
          className="mb-6"
        />
        <TextInput
          name="password"
          label="{passwordText[lang]}*"
          bind:value="{form.password}"
          type="password"
          errors="{errors.password}"
          hint="{createYourPasswordWithAtLeastXCharacteres[lang](8)}"
          className="mb-12"
        />
        <Submit
          label="{nextText[lang]}"
          color="primary"
          className="mb-6"
          classNameLabel="font-size-xl"
        />
        {#if !hideAlreadyRegistered}
          <div class="text-center">
            <Link href="/login{location.search}" class="font-size-normal">
              {renewingOrAlreadyRegisteredText[lang]}
            </Link>
          </div>
        {/if}
        <div class="text-sm text-center mt-8">
          {byRegisteringYouAgreeToTheText[lang]}<a
            use:link
            href="/terms/terms-of-service"
            class="external-link">{termsOfServiceText[lang]}</a
          >
          {andText[lang]}
          <a use:link href="/terms/privacy-policy" class="external-link"
            >{privacyPolicyText[$workflowLang]}</a
          >
        </div>

        {#if $registrationPageShowInfoBox}
          <div class="mt-8 bg-blue-200 p-4 pt-6 pb-6 mb-10 rounded-xl">
            <h4 class="text-center font-semibold">
              {@html safeInfoBoxHeader}
            </h4>
            {#if $registrationInfoBoxDescription}
              <p class="mt-4">{@html safeInfoBoxDescription}</p>
            {/if}
          </div>
        {/if}
      </form>
    </div>
  </div>
</FullLayout>
